import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  DollarSign,
  Calendar,
  Download,
  Eye,
  Plus,
  FileText,
  CreditCard,
  Building,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter,
  Search
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const PaymentsSection = ({ candidateId }) => {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadPayments();
  }, [candidateId, selectedYear, selectedMonth]);

  const loadPayments = async () => {
    try {
      setIsLoading(true);
      // Simuler des données de paie pour l'instant
      // TODO: Remplacer par l'API réelle
      const mockPayments = [
        {
          id: 1,
          month: "Janvier 2024",
          client: "TechCorp Solutions",
          mission: "Développeur Full Stack",
          grossSalary: 9800,
          netSalary: 7650,
          workingDays: 22,
          dailyRate: 450,
          status: "Payé",
          paymentDate: "2024-02-05",
          payslipUrl: "/payslips/2024-01-candidat.pdf",
          deductions: {
            socialCharges: 1850,
            taxes: 300
          },
          bonuses: {
            performance: 200,
            transport: 150
          }
        },
        {
          id: 2,
          month: "Décembre 2023",
          client: "Digital Innovations",
          mission: "Chef de Projet IT",
          grossSalary: 12600,
          netSalary: 9800,
          workingDays: 21,
          dailyRate: 600,
          status: "En attente",
          paymentDate: null,
          payslipUrl: null,
          deductions: {
            socialCharges: 2400,
            taxes: 400
          },
          bonuses: {
            yearEnd: 500,
            transport: 150
          }
        },
        {
          id: 3,
          month: "Novembre 2023",
          client: "Enterprise Systems",
          mission: "Consultant SAP",
          grossSalary: 11550,
          netSalary: 9100,
          workingDays: 21,
          dailyRate: 550,
          status: "Payé",
          paymentDate: "2023-12-05",
          payslipUrl: "/payslips/2023-11-candidat.pdf",
          deductions: {
            socialCharges: 2200,
            taxes: 250
          },
          bonuses: {
            transport: 150
          }
        }
      ];
      
      setTimeout(() => {
        setPayments(mockPayments);
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Erreur lors du chargement des paiements:", error);
      toast.error("Erreur lors du chargement des paiements");
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Payé":
        return "bg-green-100 text-green-800 border-green-200";
      case "En attente":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "En retard":
        return "bg-red-100 text-red-800 border-red-200";
      case "Traitement":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Payé":
        return <CheckCircle className="w-4 h-4" />;
      case "En attente":
        return <Clock className="w-4 h-4" />;
      case "En retard":
        return <AlertCircle className="w-4 h-4" />;
      case "Traitement":
        return <CreditCard className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR"
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non défini";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const calculateTotals = () => {
    const totalGross = payments.reduce((sum, p) => sum + p.grossSalary, 0);
    const totalNet = payments.reduce((sum, p) => sum + p.netSalary, 0);
    const totalDays = payments.reduce((sum, p) => sum + p.workingDays, 0);
    
    return { totalGross, totalNet, totalDays };
  };

  const { totalGross, totalNet, totalDays } = calculateTotals();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec filtres */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Paie & Rémunération</h2>
          <p className="text-gray-600 mt-1">
            Historique des paiements et bulletins de salaire
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={2024}>2024</option>
            <option value={2023}>2023</option>
            <option value={2022}>2022</option>
          </select>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Total Brut</span>
          </div>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {formatCurrency(totalGross)}
          </p>
          <div className="flex items-center space-x-1 mt-1">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-600">+12% vs année précédente</span>
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Total Net</span>
          </div>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {formatCurrency(totalNet)}
          </p>
          <div className="flex items-center space-x-1 mt-1">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-600">+8% vs année précédente</span>
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Jours Travaillés</span>
          </div>
          <p className="text-2xl font-bold text-purple-900 mt-1">
            {totalDays}
          </p>
          <div className="flex items-center space-x-1 mt-1">
            <span className="text-xs text-purple-600">Sur {payments.length} missions</span>
          </div>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-orange-900">Bulletins</span>
          </div>
          <p className="text-2xl font-bold text-orange-900 mt-1">
            {payments.filter(p => p.payslipUrl).length}
          </p>
          <div className="flex items-center space-x-1 mt-1">
            <span className="text-xs text-orange-600">Disponibles</span>
          </div>
        </div>
      </div>

      {/* Liste des paiements */}
      {payments.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun paiement enregistré
          </h3>
          <p className="text-gray-600">
            Les paiements apparaîtront ici une fois les missions terminées
          </p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {payments.map((payment, index) => (
            <motion.div
              key={payment.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {payment.month}
                    </h3>
                    <p className="text-gray-600 flex items-center space-x-1">
                      <Building className="w-4 h-4" />
                      <span>{payment.client} - {payment.mission}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(payment.status)}`}>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(payment.status)}
                      <span>{payment.status}</span>
                    </div>
                  </span>
                  <div className="flex space-x-1">
                    {payment.payslipUrl && (
                      <motion.button
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        title="Voir le bulletin"
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                    )}
                    {payment.payslipUrl && (
                      <motion.button
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        title="Télécharger le bulletin"
                      >
                        <Download className="w-4 h-4" />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Salaire Brut</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(payment.grossSalary)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Salaire Net</p>
                  <p className="text-lg font-semibold text-green-900">
                    {formatCurrency(payment.netSalary)}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Jours Travaillés</p>
                  <p className="text-lg font-semibold text-blue-900">
                    {payment.workingDays} jours
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Taux Journalier</p>
                  <p className="text-lg font-semibold text-purple-900">
                    {formatCurrency(payment.dailyRate)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Déductions</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Charges sociales:</span>
                      <span className="text-red-600">-{formatCurrency(payment.deductions.socialCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Impôts:</span>
                      <span className="text-red-600">-{formatCurrency(payment.deductions.taxes)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Primes & Bonus</h4>
                  <div className="space-y-1">
                    {Object.entries(payment.bonuses).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="text-green-600">+{formatCurrency(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {payment.paymentDate && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Payé le {formatDate(payment.paymentDate)}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentsSection;
